import React, { useState, useEffect, useCallback } from 'react';
import ReportList from '../components/Reports/ReportList';
import ReportForm from '../components/Reports/ReportForm';
import ReportsTable from '../components/Reports/ReportsTable';
import { CreateRapportRequest, UpdateRapportRequest, Rapport, reportService } from '../services/reportService';
import { useModal } from '../contexts/ModalContext';
import './Reports.css';

const Reports: React.FC = () => {
  const [reports, setReports] = useState<Rapport[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const { showModal } = useModal();

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const data = await reportService.getAllReports();
      setReports(data);
    } catch (err: any) {
      showModal({
        title: 'Erreur de chargement',
        message: err.response?.data?.message || 'Erreur lors du chargement des rapports',
        type: 'error',
        confirmText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  }, [showModal]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleCreateReport = async (data: CreateRapportRequest) => {
    setLoading(true);
    try {
      await reportService.uploadReport(data);
      showModal({
        title: 'Rapport créé',
        message: `Le rapport "${data.nomRapport}" a été créé avec succès.`,
        type: 'success',
        confirmText: 'OK'
      });
      setShowForm(false);
      await loadReports();
    } catch (err: any) {
      showModal({
        title: 'Erreur de création',
        message: err.response?.data?.message || 'Erreur lors de la création du rapport',
        type: 'error',
        confirmText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateReport = async (id: number, data: UpdateRapportRequest) => {
    try {
      await reportService.updateReport(id, data);
      showModal({
        title: 'Rapport mis à jour',
        message: `Le rapport "${data.nomRapport}" a été mis à jour avec succès.`,
        type: 'success',
        confirmText: 'OK'
      });
      await loadReports();
    } catch (err: any) {
      showModal({
        title: 'Erreur de mise à jour',
        message: err.response?.data?.message || 'Erreur lors de la mise à jour du rapport',
        type: 'error',
        confirmText: 'OK'
      });
      throw err;
    }
  };

  const handleReplaceFile = async (id: number, file: File) => {
    try {
      await reportService.replaceReportFile(id, file);
      showModal({
        title: 'Fichier remplacé',
        message: 'Le fichier du rapport a été remplacé avec succès.',
        type: 'success',
        confirmText: 'OK'
      });
      await loadReports();
    } catch (err: any) {
      showModal({
        title: 'Erreur de remplacement',
        message: err.response?.data?.message || 'Erreur lors du remplacement du fichier',
        type: 'error',
        confirmText: 'OK'
      });
      throw err;
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await reportService.toggleReportStatus(id);
      await loadReports();
    } catch (err: any) {
      showModal({
        title: 'Erreur de changement de statut',
        message: err.response?.data?.message || 'Erreur lors du changement de statut',
        type: 'error',
        confirmText: 'OK'
      });
      throw err;
    }
  };

  const handleDeleteReport = async (id: number) => {
    try {
      await reportService.deleteReport(id);
      showModal({
        title: 'Rapport supprimé',
        message: 'Le rapport a été supprimé avec succès.',
        type: 'success',
        confirmText: 'OK'
      });
      await loadReports();
    } catch (err: any) {
      showModal({
        title: 'Erreur de suppression',
        message: err.response?.data?.message || 'Erreur lors de la suppression du rapport',
        type: 'error',
        confirmText: 'OK'
      });
      throw err;
    }
  };

  const handleDownloadReport = async (id: number, nomRapport: string) => {
    try {
      const blob = await reportService.downloadReport(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${nomRapport}.trdp`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      showModal({
        title: 'Erreur de téléchargement',
        message: err.response?.data?.message || 'Erreur lors du téléchargement du rapport',
        type: 'error',
        confirmText: 'OK'
      });
    }
  };

  return (
    <div className="reports-page">
      <div className="reports-header">
        <h1>Gestion des Rapports</h1>
        <div className="header-actions">
          <div className="view-mode-toggle">
            <button
              className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('table')}
              title="Vue tableau"
            >
              📊
            </button>
            <button
              className={`btn ${viewMode === 'cards' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('cards')}
              title="Vue cartes"
            >
              🃏
            </button>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
            disabled={loading}
          >
            Nouveau Rapport
          </button>
        </div>
      </div>

      {showForm && (
        <ReportForm
          onSubmit={handleCreateReport}
          onCancel={() => setShowForm(false)}
          loading={loading}
        />
      )}

      {viewMode === 'table' ? (
        <ReportsTable
          reports={reports}
          onUpdateReport={handleUpdateReport}
          onReplaceFile={handleReplaceFile}
          onToggleStatus={handleToggleStatus}
          onDeleteReport={handleDeleteReport}
          onDownloadReport={handleDownloadReport}
          loading={loading}
        />
      ) : (
        <ReportList />
      )}
    </div>
  );
};

export default Reports;


